import {
  STORE_FONT_KEYS, STORE_HEADER_STYLES,
  type CheckoutPaymentMethod, type CmsSettings, type CmsSettingsUpdate, type StoreFontKey, type StoreHeaderStyle,
} from '@erp/shared';
import { ExternalLink, Globe, Image as ImageIcon, Loader2, MapPin, ShoppingCart, Trash2, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import { Button, FormField, Input } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { deleteAttachment, fetchAttachmentObjectUrl, uploadAttachment } from '../../lib/attachments.js';
import { useAuthStore } from '../../lib/authStore.js';
import { useEnabledLanguages } from '../../lib/languagesStore.js';
import { PALETTES, swatchAccent } from '../../lib/palette.js';
import { deriveStoreTheme, fontVar } from '../../lib/storeTheme.js';

import { TranslationEditor } from './TranslationEditor.js';

const HEX = /^#[0-9a-fA-F]{6}$/;
const FONT_LABELS: Record<StoreFontKey, string> = { geist: 'Geist', inter: 'Inter', poppins: 'Poppins', cairo: 'Cairo (عربي)' };

// Hero banner: a wide raster image. Mirror the public-media raster allow-list
// (SVG excluded — it can carry script). 8 MB is generous for a banner.
const HERO_MAX_BYTES = 8 * 1024 * 1024;
const HERO_MIME = new Set<string>(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

export function CmsSettingsPage(): JSX.Element {
  const { t } = useTranslation('cms');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('cms.write');
  const tenantId = useAuthStore((s) => s.tenant?.id ?? s.user?.tenantId ?? null);
  const [settings, setSettings] = useState<CmsSettings | null>(null);
  const [form, setForm] = useState<CmsSettingsUpdate>({});
  const [saving, setSaving] = useState(false);

  const hydrate = (s: CmsSettings): void => {
    setSettings(s);
    setForm({
      storeName: s.storeName,
      tagline: s.tagline ?? '',
      themeColor: s.themeColor,
      secondaryColor: s.secondaryColor,
      fontFamily: s.fontFamily,
      themePreset: s.themePreset,
      headerStyle: s.headerStyle,
      logoAttachmentId: s.logoAttachmentId,
      heroImageAttachmentId: s.heroImageAttachmentId,
      enabledLocales: s.enabledLocales,
      contactEmail: s.contactEmail ?? '',
      contactPhone: s.contactPhone ?? '',
      currencyCode: s.currencyCode,
      minOrderAmount: s.minOrderAmount ?? '',
      whatsappOrderNumber: s.whatsappOrderNumber ?? '',
      buyNowEnabled: s.buyNowEnabled,
      paymentMethods: s.paymentMethods,
      socialLinks: s.socialLinks ?? {},
      isPublished: s.isPublished,
    });
  };

  useEffect(() => {
    void api().get<CmsSettings>('/cms/settings').then((r) => hydrate(r.data)).catch(() => undefined);
  }, []);

  const set = <K extends keyof CmsSettingsUpdate>(k: K, v: CmsSettingsUpdate[K]): void =>
    setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k: string, v: string): void =>
    setForm((f) => ({ ...f, socialLinks: { ...(f.socialLinks ?? {}), [k]: v } }));
  // Picking a preset fills the colour pickers (which stay individually editable).
  const applyPreset = (id: string, primary: string, accent: string): void =>
    setForm((f) => ({ ...f, themePreset: id, themeColor: primary, secondaryColor: accent }));
  // Storefront languages to offer = the tenant's enabled catalog languages.
  const storeLangs = useEnabledLanguages();
  const toggleLocale = (l: string): void =>
    setForm((f) => {
      const cur = f.enabledLocales ?? ['en', 'ar'];
      const next = cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l];
      return { ...f, enabledLocales: next.length > 0 ? next : cur };
    });
  const togglePaymentMethod = (m: CheckoutPaymentMethod): void =>
    setForm((f) => {
      const cur = f.paymentMethods ?? [];
      return { ...f, paymentMethods: cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m] };
    });

  const save = async (): Promise<void> => {
    if (form.themeColor && !HEX.test(form.themeColor)) {
      toast.error(t('settings.badColor'));
      return;
    }
    if (form.secondaryColor && !HEX.test(form.secondaryColor)) {
      toast.error(t('settings.badColor'));
      return;
    }
    setSaving(true);
    try {
      // Normalise empty strings to null where the field is nullable.
      const payload: CmsSettingsUpdate = {
        storeName: form.storeName?.trim() || settings?.storeName,
        tagline: form.tagline?.trim() ? form.tagline.trim() : null,
        themeColor: form.themeColor || null,
        secondaryColor: form.secondaryColor || null,
        fontFamily: form.fontFamily ?? null,
        themePreset: form.themePreset ?? null,
        headerStyle: form.headerStyle ?? 'standard',
        logoAttachmentId: form.logoAttachmentId ?? null,
        heroImageAttachmentId: form.heroImageAttachmentId ?? null,
        enabledLocales: form.enabledLocales && form.enabledLocales.length > 0 ? form.enabledLocales : ['en', 'ar'],
        contactEmail: form.contactEmail?.trim() ? form.contactEmail.trim() : null,
        contactPhone: form.contactPhone?.trim() ? form.contactPhone.trim() : null,
        currencyCode: form.currencyCode?.trim() ? form.currencyCode.trim().toUpperCase() : null,
        // Delivery cities are managed on their own page (Delivery → Delivery cities);
        // this form no longer touches them, so it can't clobber edits made there.
        minOrderAmount: form.minOrderAmount?.trim() ? form.minOrderAmount.trim() : null,
        whatsappOrderNumber: form.whatsappOrderNumber?.trim() ? form.whatsappOrderNumber.trim() : null,
        buyNowEnabled: form.buyNowEnabled,
        paymentMethods: form.paymentMethods,
        socialLinks: form.socialLinks ?? null,
        isPublished: form.isPublished,
      };
      const r = await api().put<CmsSettings>('/cms/settings', payload);
      hydrate(r.data);
      toast.success(t('settings.saved'));
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <Card className="p-6"><Skeleton className="h-64 w-full" /></Card>;

  const storeUrl = `${window.location.origin}/s/${settings.slug}`;
  const social = form.socialLinks ?? {};
  // Live preview: the same token derivation the storefront uses, scoped here.
  const previewStyle = {
    ...deriveStoreTheme(form.themeColor ?? null, form.secondaryColor ?? null),
    ...fontVar(form.fontFamily ?? null),
  } as CSSProperties;

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">{t('settings.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('settings.subtitle')}</p>
        </div>
        <Badge variant={settings.isPublished ? 'success' : 'secondary'}>
          {settings.isPublished ? t('settings.published') : t('settings.draft')}
        </Badge>
      </header>

      <Card className="flex items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-2 text-sm">
          <Globe className="h-4 w-4 text-fg-muted" aria-hidden />
          <span className="text-fg-muted">{t('settings.storeUrl')}:</span>
          <a href={storeUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-mono text-brand hover:underline">
            /s/{settings.slug}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
        </div>
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <FormField id="store-name" label={t('settings.storeName')} required>
          <Input id="store-name" value={form.storeName ?? ''} onChange={(e) => set('storeName', e.target.value)} disabled={!canWrite} maxLength={200} />
        </FormField>
        <FormField id="tagline" label={t('settings.tagline')}>
          <Input id="tagline" value={form.tagline ?? ''} onChange={(e) => set('tagline', e.target.value)} disabled={!canWrite} maxLength={300} />
        </FormField>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="currency" label={t('settings.currency')} helperText={t('settings.currencyHint', { code: settings.baseCurrencyCode })}>
            <CurrencySelect id="currency" value={form.currencyCode ?? ''} onChange={(c) => set('currencyCode', c)} disabled={!canWrite} includeEmpty emptyLabel={settings.baseCurrencyCode} />
          </FormField>
          <FormField id="contact-email" label={t('settings.contactEmail')}>
            <Input id="contact-email" type="email" value={form.contactEmail ?? ''} onChange={(e) => set('contactEmail', e.target.value)} disabled={!canWrite} />
          </FormField>
          <FormField id="contact-phone" label={t('settings.contactPhone')}>
            <Input id="contact-phone" value={form.contactPhone ?? ''} onChange={(e) => set('contactPhone', e.target.value)} disabled={!canWrite} maxLength={32} />
          </FormField>
        </div>
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('settings.deliverySection', { defaultValue: 'Delivery & orders' })}</h2>
        <FormField
          id="min-order"
          label={t('settings.minOrder', { defaultValue: 'Minimum order amount' })}
          helperText={t('settings.minOrderHint', { defaultValue: 'Orders below this amount can’t be checked out. Leave blank for no minimum.' })}
        >
          <div className="flex items-center gap-2">
            <Input id="min-order" type="number" min="0" inputMode="decimal" value={form.minOrderAmount ?? ''} onChange={(e) => set('minOrderAmount', e.target.value)} disabled={!canWrite} className="max-w-[12rem]" />
            <span className="text-sm text-fg-muted">{form.currencyCode || settings.currencyCode || settings.baseCurrencyCode}</span>
          </div>
        </FormField>
        <FormField
          id="wa-order"
          label={t('settings.whatsappOrder', { defaultValue: 'WhatsApp ordering number' })}
          helperText={t('settings.whatsappOrderHint', { defaultValue: 'Shows an “Order via WhatsApp” button at checkout that sends the cart + customer details to this number. Include the country code (e.g. +964…). Leave blank to hide.' })}
        >
          <Input id="wa-order" inputMode="tel" placeholder="+964 750 000 0000" value={form.whatsappOrderNumber ?? ''} onChange={(e) => set('whatsappOrderNumber', e.target.value)} disabled={!canWrite} className="max-w-xs" />
        </FormField>
        <FormField
          id="payment-methods"
          label={t('settings.paymentMethods', { defaultValue: 'Payment methods' })}
          helperText={t('settings.paymentMethodsHint', { defaultValue: 'Which payment options customers can choose at checkout. FIB (pay online) only appears when FIB online payment is configured.' })}
        >
          <div className="flex flex-col gap-2">
            {(['fib', 'cod', 'bank_transfer', 'pay_later'] as CheckoutPaymentMethod[]).map((m) => (
              <label key={m} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={(form.paymentMethods ?? []).includes(m)}
                  onChange={() => togglePaymentMethod(m)}
                  disabled={!canWrite}
                  className="h-4 w-4"
                />
                <span>{t(`checkout.method.${m}`, { defaultValue: m })}</span>
              </label>
            ))}
          </div>
        </FormField>
        <FormField
          id="buy-now"
          label={t('settings.buyNow', { defaultValue: 'Buy now button' })}
          helperText={t('settings.buyNowHint', { defaultValue: 'Show a “Buy now” button on product pages (mobile app) that jumps straight to checkout.' })}
        >
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.buyNowEnabled} onChange={(e) => set('buyNowEnabled', e.target.checked)} disabled={!canWrite} className="h-4 w-4" />
            <span>{t('settings.buyNowShow', { defaultValue: 'Show the Buy now button' })}</span>
          </label>
        </FormField>
        <FormField
          id="cities"
          label={t('settings.cities', { defaultValue: 'Delivery cities' })}
          helperText={t('settings.citiesMovedHint', { defaultValue: 'Delivery cities and their fees are now managed on their own page under Delivery.' })}
        >
          <Link
            to="/delivery/cities"
            className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-subtle px-3 py-2 text-sm font-medium text-brand transition hover:bg-bg-muted"
          >
            <MapPin className="h-4 w-4" aria-hidden />
            {t('settings.manageCities', { defaultValue: 'Manage delivery cities' })}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </Link>
        </FormField>
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('settings.themeSection')}</h2>

        <FormField id="theme-preset" label={t('settings.preset')} helperText={t('settings.presetHint')}>
          <div className="flex flex-wrap gap-2">
            {PALETTES.map((p) => {
              const accent = swatchAccent(p);
              const active = form.themePreset === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={!canWrite}
                  onClick={() => applyPreset(p.id, p.primary, accent)}
                  title={p.name}
                  aria-pressed={active}
                  className={`flex items-center gap-1 rounded-md border p-1 ${active ? 'border-brand ring-2 ring-brand/30' : 'border-border'}`}
                >
                  <span className="h-5 w-5 rounded-sm" style={{ backgroundColor: p.primary }} aria-hidden />
                  <span className="h-5 w-5 rounded-sm" style={{ backgroundColor: accent }} aria-hidden />
                </button>
              );
            })}
          </div>
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="theme-color" label={t('settings.themeColor')} helperText={t('settings.themeColorHint')}>
            <div className="flex items-center gap-2">
              <span aria-hidden className="h-9 w-9 flex-none rounded border border-border" style={{ backgroundColor: form.themeColor && HEX.test(form.themeColor) ? form.themeColor : 'transparent' }} />
              <Input id="theme-color" value={form.themeColor ?? ''} onChange={(e) => set('themeColor', e.target.value)} disabled={!canWrite} />
            </div>
          </FormField>
          <FormField id="secondary-color" label={t('settings.secondaryColor')} helperText={t('settings.secondaryColorHint')}>
            <div className="flex items-center gap-2">
              <span aria-hidden className="h-9 w-9 flex-none rounded border border-border" style={{ backgroundColor: form.secondaryColor && HEX.test(form.secondaryColor) ? form.secondaryColor : 'transparent' }} />
              <Input id="secondary-color" value={form.secondaryColor ?? ''} onChange={(e) => set('secondaryColor', e.target.value)} disabled={!canWrite} />
            </div>
          </FormField>
          <FormField id="font-family" label={t('settings.fontFamily')} helperText={t('settings.fontHint')}>
            <select
              id="font-family"
              value={form.fontFamily ?? ''}
              onChange={(e) => set('fontFamily', (e.target.value || null) as StoreFontKey | null)}
              disabled={!canWrite}
              className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm text-fg outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
            >
              <option value="">{t('settings.fontDefault')}</option>
              {STORE_FONT_KEYS.map((k) => <option key={k} value={k}>{FONT_LABELS[k]}</option>)}
            </select>
          </FormField>
          <FormField id="header-style" label={t('settings.headerStyle')}>
            <select
              id="header-style"
              value={form.headerStyle ?? 'standard'}
              onChange={(e) => set('headerStyle', e.target.value as StoreHeaderStyle)}
              disabled={!canWrite}
              className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm text-fg outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
            >
              {STORE_HEADER_STYLES.map((h) => <option key={h} value={h}>{t(`settings.headerStyles.${h}`)}</option>)}
            </select>
          </FormField>
        </div>

        <FormField
          id="store-logo"
          label={t('settings.logo.label', { defaultValue: 'Store logo' })}
          helperText={t('settings.logo.hint', { defaultValue: 'Shown at the top of your store. A square image works best.' })}
        >
          <HeroUpload
            tenantId={tenantId}
            entityType="cms_logo"
            square
            attachmentId={form.logoAttachmentId ?? null}
            disabled={!canWrite}
            onChange={(id) => set('logoAttachmentId', id)}
          />
        </FormField>

        <FormField id="hero-image" label={t('settings.hero.label')} helperText={t('settings.hero.hint')}>
          <HeroUpload
            tenantId={tenantId}
            attachmentId={form.heroImageAttachmentId ?? null}
            disabled={!canWrite}
            onChange={(id) => set('heroImageAttachmentId', id)}
          />
        </FormField>

        <div>
          <div className="mb-1.5 text-xs font-medium text-fg-muted">{t('settings.preview')}</div>
          <div style={previewStyle} className="overflow-hidden rounded-lg border border-border font-sans">
            <div className={`flex px-3 text-brand-contrast ${
              form.headerStyle === 'centered' ? 'flex-col items-center gap-1 bg-brand py-2'
                : form.headerStyle === 'glass' ? 'm-1.5 items-center justify-between rounded-lg border border-white/30 bg-brand/70 py-1.5 shadow-sm backdrop-blur-sm'
                  : `items-center justify-between bg-brand ${form.headerStyle === 'minimal' ? 'py-1.5' : 'py-2'}`
            }`}>
              <span className={`font-semibold ${form.headerStyle === 'minimal' ? 'text-xs' : 'text-sm'}`}>{form.storeName || settings.storeName}</span>
              <ShoppingCart className="h-4 w-4" aria-hidden />
            </div>
            <div className="flex flex-col items-center gap-2 px-3 py-5 text-center">
              <div className="text-base font-bold">{form.tagline || form.storeName || settings.storeName}</div>
              <div className="flex w-full max-w-[14rem] items-center justify-between rounded-md border border-border bg-bg-subtle p-2 text-start">
                <span className="text-xs">{t('settings.previewItem')}</span>
                <span className="rounded-md bg-brand px-2 py-1 text-[0.65rem] font-semibold text-brand-contrast">{t('settings.previewCta')}</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('settings.languages')}</h2>
        <FormField id="enabled-locales" label={t('settings.enabledLocales')} helperText={t('settings.enabledLocalesHint')}>
          <div className="flex flex-wrap gap-4">
            {storeLangs.map((l) => (
              <label key={l.code} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={(form.enabledLocales ?? ['en', 'ar']).includes(l.code)}
                  disabled={!canWrite}
                  onChange={() => toggleLocale(l.code)}
                />
                <span dir={l.isRtl ? 'rtl' : 'ltr'}>{l.nativeName}{l.code === settings.defaultLocale ? ` · ${t('settings.defaultLocale')}` : ''}</span>
              </label>
            ))}
          </div>
        </FormField>
        <div className="border-t border-border pt-3">
          <h3 className="mb-2 text-xs font-medium text-fg-muted">{t('settings.translateContent')}</h3>
          <TranslationEditor
            entityType="cms_settings"
            disabled={!canWrite}
            fields={[
              { key: 'store_name', label: t('settings.storeName') },
              { key: 'tagline', label: t('settings.tagline') },
            ]}
          />
        </div>
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('settings.social')}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="s-website" label={t('settings.website')}>
            <Input id="s-website" value={social.website ?? ''} onChange={(e) => setSocial('website', e.target.value)} disabled={!canWrite} placeholder="https://" />
          </FormField>
          <FormField id="s-instagram" label="Instagram">
            <Input id="s-instagram" value={social.instagram ?? ''} onChange={(e) => setSocial('instagram', e.target.value)} disabled={!canWrite} placeholder="https://" />
          </FormField>
          <FormField id="s-facebook" label="Facebook">
            <Input id="s-facebook" value={social.facebook ?? ''} onChange={(e) => setSocial('facebook', e.target.value)} disabled={!canWrite} placeholder="https://" />
          </FormField>
          <FormField id="s-whatsapp" label="WhatsApp">
            <Input id="s-whatsapp" value={social.whatsapp ?? ''} onChange={(e) => setSocial('whatsapp', e.target.value)} disabled={!canWrite} />
          </FormField>
        </div>
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('settings.appDownload')}</h2>
          <p className="mt-1 text-xs text-fg-muted">{t('settings.appDownloadHint')}</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="s-app-android" label={t('settings.appAndroid')}>
            <Input id="s-app-android" value={social.appAndroid ?? ''} onChange={(e) => setSocial('appAndroid', e.target.value)} disabled={!canWrite} placeholder="https://play.google.com/store/apps/…" />
          </FormField>
          <FormField id="s-app-ios" label={t('settings.appIos')}>
            <Input id="s-app-ios" value={social.appIos ?? ''} onChange={(e) => setSocial('appIos', e.target.value)} disabled={!canWrite} placeholder="https://apps.apple.com/app/…" />
          </FormField>
        </div>
      </Card>

      {canWrite && (
        <Card className="flex flex-wrap items-center justify-between gap-3 p-5">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.isPublished} onChange={(e) => set('isPublished', e.target.checked)} className="h-4 w-4" />
            <span>{t('settings.publishLabel')}</span>
          </label>
          <Button onClick={() => void save()} loading={saving}>{t('settings.save')}</Button>
        </Card>
      )}
    </div>
  );
}

interface HeroUploadProps {
  /** Tenant id — the attachment's entity_id. */
  tenantId: string | null;
  /** Currently-selected attachment id (form state), or null. */
  attachmentId: string | null;
  disabled: boolean;
  /** Fires with the new attachment id after a successful upload, or null on remove. */
  onChange: (id: string | null) => void;
  /** Attachment entity_type — 'cms_hero' (wide banner) or 'cms_logo' (square). */
  entityType?: string;
  /** Square preview tile (for the logo) instead of the wide 16:9 hero tile. */
  square?: boolean;
}

/**
 * Hero banner uploader. Posts the raw bytes via the generic attachments API
 * (entity_type='cms_hero', entity_id=tenantId) and propagates the new
 * attachment id; the storefront serves it publicly through getMedia once the
 * store is published. Previews are fetched through the authed client (the
 * staff side can't use a bare <img src>), so we wrap the bytes in an object URL
 * and revoke it on change/unmount — same contract as the category photo picker.
 */
function HeroUpload({ tenantId, attachmentId, disabled, onChange, entityType = 'cms_hero', square = false }: HeroUploadProps): JSX.Element {
  const { t } = useTranslation('cms');
  const inputRef = useRef<HTMLInputElement>(null);
  const objUrlRef = useRef<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const show = useCallback((next: string | null): void => {
    if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current);
    objUrlRef.current = next;
    setPreview(next);
  }, []);

  // Load (or clear) the preview whenever the saved attachment id changes.
  useEffect(() => {
    let active = true;
    if (!attachmentId) { show(null); return; }
    void (async () => {
      try {
        const u = await fetchAttachmentObjectUrl(attachmentId);
        if (active) show(u); else URL.revokeObjectURL(u);
      } catch { if (active) show(null); }
    })();
    return () => { active = false; };
  }, [attachmentId, show]);

  // Revoke the last object URL on unmount.
  useEffect(() => () => { if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current); }, []);

  const onFile = async (file: File): Promise<void> => {
    setError(null);
    if (!tenantId) return;
    if (!HERO_MIME.has(file.type)) { setError(t('settings.hero.mimeError')); return; }
    if (file.size > HERO_MAX_BYTES) { setError(t('settings.hero.sizeError')); return; }
    setBusy(true);
    try {
      const att = await uploadAttachment(entityType, tenantId, file);
      // Best-effort cleanup of the previous (possibly unsaved) hero so replacing
      // doesn't leave orphaned blobs behind.
      if (attachmentId && attachmentId !== att.id) await deleteAttachment(attachmentId).catch(() => undefined);
      show(URL.createObjectURL(file));
      onChange(att.id);
    } catch {
      setError(t('settings.hero.uploadError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-start gap-3">
      <div className={`relative ${square ? 'aspect-square w-24' : 'aspect-[16/9] w-40'} flex-none overflow-hidden rounded-md border border-border bg-bg-subtle`}>
        {preview
          ? <img src={preview} alt="" className="absolute inset-0 h-full w-full object-cover" />
          : <div className="grid h-full w-full place-items-center text-fg-muted"><ImageIcon className="h-6 w-6" aria-hidden /></div>}
      </div>
      <div className="flex-1">
        <input
          ref={inputRef}
          type="file"
          accept={[...HERO_MIME].join(',')}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onFile(f);
            if (e.target) e.target.value = '';
          }}
        />
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" disabled={disabled || busy} onClick={() => inputRef.current?.click()}>
            {busy
              ? <><Loader2 className="h-4 w-4 animate-spin" aria-hidden />{t('settings.hero.uploading')}</>
              : <><Upload className="h-4 w-4" aria-hidden />{preview ? t('settings.hero.replace') : t('settings.hero.pick')}</>}
          </Button>
          {preview && (
            <Button type="button" variant="ghost" size="sm" disabled={disabled || busy} onClick={() => { show(null); onChange(null); }}>
              <Trash2 className="h-4 w-4" aria-hidden />{t('settings.hero.remove')}
            </Button>
          )}
        </div>
        {error && <p className="mt-1 text-xs text-danger">{error}</p>}
      </div>
    </div>
  );
}
